import { VUEX_EXERCISE_GETTER_NAMES } from "@tt-app/constants";
import { ExerciseState } from "@tt-app/types";

const {
  ANSWERS,
  ASSIGNMENTS_SUBMITTED,
  BODY,
  CURRENT_PAGE,
  DETAILS,
  EXERCISE_ACCURACY,
  EXERCISE_PAGES,
  EXERCISE_RESULTS,
  EXERCISE_TYPE,
  FINAL_QUESTION,
  FOOTER,
  LOADING,
  LOG_ID,
  LOG_ANSWER_PARAMS,
  LOG_END_PARAMS,
  LOG_START_PARAMS,
  PAGE_TYPE,
  PROBLEM_UUID,
  PROCESSING,
  REVIEW_PAGE
} = VUEX_EXERCISE_GETTER_NAMES;

const exerciseGetters = {
  [ANSWERS](state: any) {
    return state.answers;
  },

  [ASSIGNMENTS_SUBMITTED](state: ExerciseState) {
    return state.results?.numAssignmentsSubmitted || 0;
  },

  [BODY](state: any, getters: any) {
    const currentPage = getters[CURRENT_PAGE];

    if (!currentPage) return undefined;

    const pageBody = currentPage.body;

    if (pageBody?.[0]?.type === "REVIEW") {
      return getters[REVIEW_PAGE]?.body;
    }

    return pageBody;
  },

  [CURRENT_PAGE](state: any) {
    const { currentPageIndex, details } = state;

    const pages = details?.exercise?.pages;

    if (!pages) {
      return undefined;
    }

    return pages[currentPageIndex];
  },

  [FOOTER](state: any, getters: any) {
    const { currentPageIndex, details, results } = state;

    const pageType = getters[PAGE_TYPE];

    if (pageType === "REVIEW") {
      return results?.reviewPage?.footer;
    }

    const pages = details?.exercise?.pages;

    if (!pages) {
      return undefined;
    }

    return pages[currentPageIndex].footer;
  },

  [DETAILS](state: any) {
    return state.details;
  },

  [EXERCISE_PAGES](state: ExerciseState) {
    return state.details?.exercise?.pages;
  },

  [EXERCISE_ACCURACY](state: any) {
    return state.accuracy;
  },

  [EXERCISE_RESULTS](state: any) {
    return state.results;
  },

  [EXERCISE_TYPE](state: any) {
    if (!state.details) return undefined;

    return state.details.exercise.type;
  },

  [FINAL_QUESTION](state: any, getters: any) {
    const details = getters[DETAILS];
    const currentPageIndex = state.currentPageIndex;

    const nextPage = details?.exercise?.pages?.[currentPageIndex + 1];

    return !nextPage?.footer?.[0]?.problemUuid;
  },

  [LOADING](state: any) {
    return state.loading;
  },

  [LOG_ID](state: any) {
    return state.logId;
  },

  [LOG_ANSWER_PARAMS](state: any, getters: any) {
    const startParams = getters[LOG_START_PARAMS];
    const problemUuid = getters[PROBLEM_UUID];
    const logId = getters[LOG_ID];

    return {
      ...startParams,
      logId,
      problemUuid
    };
  },

  [LOG_END_PARAMS](state: any, getters: any) {
    const startParams = getters[LOG_START_PARAMS];
    const logId = getters[LOG_ID];

    const numCorrectAnswers = 1;
    const numIncorrectAnswers = 2;

    return {
      ...startParams,
      logId,
      numCorrectAnswers,
      numIncorrectAnswers
    };
  },

  [LOG_START_PARAMS](state: any) {
    const courseUuid = state.details?.course.uuid;
    const exerciseType = state.details?.exercise.type;
    const exerciseUuid = state.details?.exercise.uuid;

    return {
      courseUuid,
      exerciseType,
      exerciseUuid
    };
  },

  [PAGE_TYPE](state: any, getters: any) {
    const { currentPageIndex, details } = state;

    const pages = details?.exercise?.pages;

    if (!pages) return undefined;

    const pageBody = pages[currentPageIndex].body;
    const exerciseType = getters[EXERCISE_TYPE];

    if (!pageBody || !exerciseType) return undefined;

    const standalonePages = ["EXERCISE_SCOPE", "REVIEW"];

    if (standalonePages.includes(pageBody[0].type)) {
      return pageBody[0].type;
    }

    switch (exerciseType) {
      case 13:
        return "WRITING";
      default:
        return undefined;
    }
  },

  [PROBLEM_UUID](state: any, getters: any) {
    const pageFooter = getters[FOOTER];

    if (!pageFooter) return undefined;

    return pageFooter?.[0]?.problemUuid;
  },

  [PROCESSING](state: any) {
    return state.processing;
  },

  [REVIEW_PAGE](state: any) {
    if (!state.results) return undefined;

    return state.results.reviewPage;
  }
};

export { exerciseGetters };
